import React, { type ChangeEvent, useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as EditIcon } from '@pkg/assets/icons/v2/edit.svg';
import { type ModalProps } from '@pkg/components';
import { Modal } from '@pkg/components/Modal';
import { Form } from '@pkg/components/Modal/styles';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { PrivacyOptions } from '@pkg/components/Wishlist/AddToWishlistModal/PrivacyOptions';
import { DepInput } from '@pkg/components/deprecated';

import config from '@app/config';

import {
    ButtonWrapper,
    Heading,
    InputWrapper,
    ModalLayout,
    PodsIconContainer,
    PodsIconWrapper,
    SectionWrapper,
    Subheading,
} from './styles';

const POD_NAME_LIMIT = config.podNameLimit;

export interface CreatePodFormProps {
    podName: string;
    errorMessage?: string;
    isPrivate?: boolean;
    isLoading?: boolean;
    onClose: ModalProps['onClose'];
    onCreatePod(): void;
    onInputChange(event: ChangeEvent<HTMLInputElement>): void;
    onCheckedChange(checked: boolean): void;
}

export function CreatePodForm({
    podName,
    errorMessage,
    isLoading = false,
    isPrivate = false,
    onClose,
    onCreatePod,
    onInputChange,
    onCheckedChange,
}: CreatePodFormProps) {
    const { t: translate } = useTranslation(['groups']);

    const handleSubmit = useCallback(
        (evt: React.FormEvent) => {
            evt.preventDefault();
            onCreatePod();
        },
        [onCreatePod],
    );

    const hasError = Boolean(errorMessage);

    return (
        <Modal onClose={onClose}>
            <ModalLayout>
                <PodsIconContainer>
                    <PodsIconWrapper>
                        <img src="/assets/img/pod-icon.png" alt="Pods icon" />
                    </PodsIconWrapper>
                </PodsIconContainer>
                <SectionWrapper>
                    <Heading>{translate('pod_creation_create_a_new_pod')}</Heading>
                    <Subheading>{translate('pod_creation_create_a_new_pod_subtitle')}</Subheading>
                </SectionWrapper>
                <Form onSubmit={handleSubmit}>
                    <InputWrapper $charactersLeft={POD_NAME_LIMIT - podName.length}>
                        <DepInput
                            maxLength={25}
                            value={podName}
                            label={translate('pod_creation_pod_name_input_label')}
                            aria-label={translate('pod_creation_pod_name_input_label')}
                            errorLabel={errorMessage}
                            append={<EditIcon />}
                            disabled={isLoading}
                            isInvalid={hasError}
                            onChange={onInputChange}
                        />
                    </InputWrapper>
                    <PrivacyOptions
                        heading={translate('pod_creation_privacy_heading')}
                        publicDescription={translate('pod_creation_privacy_public_desc')}
                        privateDescription={translate('pod_creation_privacy_private_desc')}
                        isPrivate={isPrivate}
                        handleRadioChange={onCheckedChange}
                    />
                    <ButtonWrapper>
                        <Button disabled={!podName || podName.length < 3} size={'large'} loading={isLoading}>
                            {translate('pod_creation_next')}
                        </Button>
                    </ButtonWrapper>
                </Form>
            </ModalLayout>
        </Modal>
    );
}
